import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Avatar,
  Chip,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  Card,
  Fade,
  alpha,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Close,
  ArrowBack,
  CalendarMonth,
  Person,
  Groups,
  EventBusy,
  Search,
  Weekend,
  Celebration
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { AttendanceStatus, InformationType } from '../../types';

// ============ Types ============
export interface EmployeeData {
  _id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
}

export interface AttendanceFormData {
  userId: string;
  status: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
}

export interface SavePayload {
  attendanceRecords: AttendanceFormData[];
  applyToAll: boolean;
  bulkStatus?: AttendanceStatus | null;
  bulkInfoType?: InformationType | null;
  bulkRemarks?: string;
}

type AttendanceMode = 'select' | 'individual' | 'bulk' | 'scheduled-off';

interface MarkAttendanceModalProps {
  open: boolean;
  onClose: () => void;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  onSave: (payload: SavePayload) => Promise<void>;
  onSearch: (query: string) => Promise<EmployeeData[]>;
  initialEmployees?: EmployeeData[];
}

// ============ Helper: Stable Debounce Hook ============
function useDebounce<T extends (...args: any[]) => void>(callback: T, delay: number) {
  const callbackRef = useRef(callback);
  useMemo(() => { callbackRef.current = callback; }, [callback]);

  return useMemo(() => {
    let timer: NodeJS.Timeout;
    const func = (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    };
    func.cancel = () => clearTimeout(timer);
    return func;
  }, [delay]);
}

// ============ Constants ============
const STATUS_OPTIONS = [
  { value: AttendanceStatus.PRESENT, label: 'Present', color: '#22c55e' },
  { value: AttendanceStatus.LATE, label: 'Late', color: '#f59e0b' },
  { value: AttendanceStatus.HALF_DAY, label: 'Half Day', color: '#3b82f6' },
  { value: AttendanceStatus.ABSENT, label: 'Absent', color: '#ef4444' },
];

const OFF_OPTIONS = [
    { value: AttendanceStatus.SCHEDULED_OFF, label: 'Holiday / Scheduled Off', icon: Celebration, color: '#ea580c' },
    { value: AttendanceStatus.NOT_SCHEDULED, label: 'Not Scheduled (off for specific employees)', icon: Weekend, color: '#64748b' },
];

const MODE_OPTIONS = [
  { id: 'individual' as const, title: 'Mark Individual', desc: 'Select single employee', icon: Person, color: '#2563eb' },
  { id: 'bulk' as const, title: 'Mark Bulk', desc: 'Select multiple employees', icon: Groups, color: '#7c3aed' },
  { id: 'scheduled-off' as const, title: 'Mark Off/Holiday', desc: 'Set day off for everyone / selected employees', icon: EventBusy, color: '#ea580c' },
];

// ============ Main Component ============
const MarkAttendanceModal: React.FC<MarkAttendanceModalProps> = ({
  open,
  onClose,
  selectedDate,
  onDateChange,
  onSave,
  onSearch,
  initialEmployees = [],
}) => {
  // State
  const [mode, setMode] = useState<AttendanceMode>('select');
  
  // Individual Mode State
  const [selectedIndividual, setSelectedIndividual] = useState<EmployeeData | null>(null);
  const [individualData, setIndividualData] = useState<AttendanceFormData>({
    userId: '',
    status: AttendanceStatus.PRESENT,
  });

  // Bulk Mode State
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeData[]>([]);
  const [selectAllActive, setSelectAllActive] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<AttendanceStatus | null>(null);
  const [bulkInfoType, setBulkInfoType] = useState<InformationType | null>(null);
  const [bulkRemarks, setBulkRemarks] = useState('');

  // Scheduled Off State (Reuse some bulk vars but distinct where needed)
  const [dayOffStatus, setDayOffStatus] = useState<AttendanceStatus | null>(null);

  // Search State
  const [searchResults, setSearchResults] = useState<EmployeeData[]>([]);
  const [searching, setSearching] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset Logic
  const resetAll = useCallback(() => {
    setMode('select');
    setSelectedIndividual(null);
    setIndividualData({ userId: '', status: AttendanceStatus.PRESENT });
    setSelectedEmployees([]);
    setSelectAllActive(false);
    setBulkStatus(null);
    setBulkInfoType(null);
    setDayOffStatus(null);
    setBulkRemarks('');
    setInputValue('');
    setSearchResults([]); 
    setSearching(false);
  }, []);

  useEffect(() => {
    if (!open) resetAll();
  }, [open, resetAll]);


  // ============ Search Logic ============
  
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const results = await onSearch(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [onSearch]);

  const debouncedSearch = useDebounce(performSearch, 500);

  useEffect(() => {
    if (inputValue) {
        debouncedSearch(inputValue);
    } else {
        setSearchResults([]);
    }
    return () => debouncedSearch.cancel();
  }, [inputValue, debouncedSearch]);


  // ============ Handlers ============

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleBack = () => {
    resetAll();
  };

  const handleAddEmployee = (emp: EmployeeData) => {
     if (selectAllActive) return;
     if (!selectedEmployees.find(e => e._id === emp._id)) {
       setSelectedEmployees(prev => [...prev, emp]);
     }
     setInputValue('');
  };

  const handleRemoveEmployee = (id: string) => {
    if (selectAllActive) return;
    setSelectedEmployees(prev => prev.filter(e => e._id !== id));
  };

  const handleIndividualSelect = (emp: EmployeeData | null) => {
    setSelectedIndividual(emp);
    if (emp) {
        setIndividualData({ userId: emp._id, status: AttendanceStatus.PRESENT });
        setInputValue('');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      let payload: SavePayload = {
        attendanceRecords: [],
        applyToAll: false,
      };

      if (mode === 'individual') {
        if (selectedIndividual) {
            payload.attendanceRecords = [{ ...individualData, userId: selectedIndividual._id }];
        }
      } else if (mode === 'bulk') {
        if (selectAllActive) {
          payload.applyToAll = true;
          payload.bulkStatus = bulkStatus;
          payload.bulkInfoType = bulkInfoType;
          payload.bulkRemarks = bulkRemarks;
        } else {
          payload.attendanceRecords = selectedEmployees.map(emp => ({
            userId: emp._id,
            status: bulkStatus!,
            informationType: bulkInfoType || undefined,
            remarks: bulkRemarks,
          }));
        }
      } else if (mode === 'scheduled-off') {
         if (selectAllActive) {
            payload.applyToAll = true;
            payload.bulkStatus = dayOffStatus;
            payload.bulkRemarks = bulkRemarks || (dayOffStatus === AttendanceStatus.SCHEDULED_OFF ? 'Scheduled Off' : 'Not Scheduled');
         } else {
            payload.attendanceRecords = selectedEmployees.map(emp => ({
                userId: emp._id,
                status: dayOffStatus!,
                remarks: bulkRemarks || (dayOffStatus === AttendanceStatus.SCHEDULED_OFF ? 'Scheduled Off' : 'Not Scheduled'),
            }));
         }
      }

      await onSave(payload);
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // ============ UI Helpers ============

  const isSaveDisabled = useMemo(() => {
      if (mode === 'select') return true;
      if (mode === 'individual') {
          if (!selectedIndividual) return true;
          if ([AttendanceStatus.LATE, AttendanceStatus.HALF_DAY, AttendanceStatus.ABSENT].includes(individualData.status) && !individualData.informationType) return true;
          return false;
      }
      if (mode === 'bulk') {
          if (!selectAllActive && selectedEmployees.length === 0) return true;
          if (!bulkStatus) return true;
          if ([AttendanceStatus.LATE, AttendanceStatus.HALF_DAY, AttendanceStatus.ABSENT].includes(bulkStatus) && !bulkInfoType) return true;
          return false;
      }
      if (mode === 'scheduled-off') {
          if (!selectAllActive && selectedEmployees.length === 0) return true;
          if (!dayOffStatus) return true;
          return false;
      }
      return true;
  }, [mode, selectedIndividual, individualData, selectAllActive, selectedEmployees, bulkStatus, bulkInfoType, dayOffStatus]);

  // ============ Renderers ============

  const renderSearchField = (
      onSelect: (emp: EmployeeData) => void, 
      placeholder: string,
      disabled: boolean = false
  ) => (
    <Autocomplete
        options={searchResults}
        getOptionLabel={(o) => o.name}
        filterOptions={(x) => x}
        value={null}
        inputValue={inputValue}
        onInputChange={(_, val, reason) => {
            if (reason === 'reset' && !val) return;
            setInputValue(val);
        }}
        onChange={(_, val) => {
            if (val) {
                onSelect(val);
            }
        }}
        disabled={disabled}
        fullWidth
        loading={searching}
        clearOnBlur={false}
        noOptionsText={
            !inputValue ? 'Type to search employees...' : 
            searching ? 'Searching...' : 
            'No employees found'
        }
        renderInput={(params) => (
            <TextField
                {...params}
                placeholder={placeholder}
                size="small"
                InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                        <InputAdornment position="start">
                             <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <>
                            {searching ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                        </>
                    )
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6),
                    }
                }}
            />
        )}
        renderOption={(props, option) => {
             // eslint-disable-next-line @typescript-eslint/no-unused-vars
             const { key, ...rest } = props as any;
             return (
                 <li key={option._id} {...rest}>
                     <Box display="flex" alignItems="center" gap={1.5} py={0.5}>
                         <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: 'primary.light' }}>{option.name[0]}</Avatar>
                         <Box>
                             <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                             <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                         </Box>
                     </Box>
                 </li>
             )
        }}
    />
  );

  const renderModeSelect = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {MODE_OPTIONS.map((opt) => (
            <Card
                key={opt.id}
                onClick={() => setMode(opt.id)}
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    cursor: 'pointer',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s',
                    '&:hover': {
                        borderColor: opt.color,
                        bgcolor: alpha(opt.color, 0.04),
                    }
                }}
            >
                <Avatar sx={{ bgcolor: alpha(opt.color, 0.1), color: opt.color }}>
                    <opt.icon />
                </Avatar>
                <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{opt.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{opt.desc}</Typography>
                </Box>
            </Card>
        ))}
    </Box>
  );

  const renderIndividual = () => (
      <Fade in timeout={300}>
          <Box sx={{ pt: 1 }}>
              {!selectedIndividual ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="subtitle1" fontWeight={500} gutterBottom>Find Employee</Typography>
                       <Box sx={{ maxWidth: 400, mx: 'auto' }}>
                        {renderSearchField(handleIndividualSelect, "Search by name, email...")}
                       </Box>
                  </Box>
              ) : (
                  <Box>
                      <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 50, height: 50, bgcolor: 'primary.main' }}>{selectedIndividual.name[0]}</Avatar>
                          <Box flex={1}>
                              <Typography variant="h6" fontSize="1rem">{selectedIndividual.name}</Typography>
                              <Typography variant="body2" color="text.secondary">{selectedIndividual.email}</Typography>
                          </Box>
                          <Button size="small" onClick={() => setSelectedIndividual(null)} sx={{ borderRadius: 2 }}>Change</Button>
                      </Card>

                       <Typography variant="subtitle2" gutterBottom>Status</Typography>
                       <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                           {STATUS_OPTIONS.map(opt => (
                               <Chip 
                                    key={opt.value}
                                    label={opt.label}
                                    onClick={() => setIndividualData(prev => ({ ...prev, status: opt.value }))}
                                    variant={individualData.status === opt.value ? 'filled' : 'outlined'}
                                    sx={{
                                        bgcolor: individualData.status === opt.value ? opt.color : 'transparent',
                                        color: individualData.status === opt.value ? '#fff' : opt.color,
                                        borderColor: opt.color,
                                        '&:hover': { 
                                             bgcolor: individualData.status === opt.value ? opt.color : alpha(opt.color, 0.1) 
                                        }
                                    }}
                               />
                           ))}
                       </Box>

                       {[AttendanceStatus.LATE, AttendanceStatus.HALF_DAY, AttendanceStatus.ABSENT].includes(individualData.status) && (
                           <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                               <Typography variant="body2" fontWeight={500} gutterBottom>Type</Typography>
                               <RadioGroup 
                                    row 
                                    value={individualData.informationType || ''}
                                    onChange={(e) => setIndividualData(prev => ({ ...prev, informationType: e.target.value as InformationType }))}
                                >
                                   <FormControlLabel value={InformationType.INFORMED} control={<Radio size="small" />} label="Informed" />
                                   <FormControlLabel value={InformationType.UNINFORMED} control={<Radio size="small" />} label="Uninformed" />
                               </RadioGroup>
                           </Box>
                       )}

                       <TextField
                           fullWidth
                           placeholder="Optional remarks"
                           multiline
                           rows={2}
                           value={individualData.remarks || ''}
                           onChange={(e) => setIndividualData(prev => ({ ...prev, remarks: e.target.value }))}
                           sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                       />
                  </Box>
              )}
          </Box>
      </Fade>
  );

  const renderBulk = () => (
      <Box sx={{ pt: 1 }}>
          <Box sx={{ mb: 3 }}>
              <FormControlLabel
                  control={<Checkbox checked={selectAllActive} onChange={(e) => setSelectAllActive(e.target.checked)} />}
                  label={<Typography fontWeight={500}>Apply to Everyone</Typography>}
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 3.5, mt: -0.5 }}>
                  Apply to all active employees in school
              </Typography>
          </Box>

          {!selectAllActive && (
              <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>Add Employees</Typography>
                  {renderSearchField(handleAddEmployee, "Search to add...")}
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={1.5}>
                      {selectedEmployees.map(emp => (
                          <Chip key={emp._id} label={emp.name} onDelete={() => handleRemoveEmployee(emp._id)} size="small" />
                      ))}
                  </Box>
              </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle2" gutterBottom>Bulk Status</Typography>
           <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
               {STATUS_OPTIONS.map(opt => (
                   <Chip 
                        key={opt.value}
                        label={opt.label}
                        onClick={() => setBulkStatus(opt.value)}
                        variant={bulkStatus === opt.value ? 'filled' : 'outlined'}
                        sx={{
                            bgcolor: bulkStatus === opt.value ? opt.color : 'transparent',
                            color: bulkStatus === opt.value ? '#fff' : opt.color,
                            borderColor: opt.color,
                            '&:hover': { bgcolor: bulkStatus === opt.value ? opt.color : alpha(opt.color, 0.1) }
                        }}
                   />
               ))}
           </Box>

            {[AttendanceStatus.LATE, AttendanceStatus.HALF_DAY, AttendanceStatus.ABSENT].includes(bulkStatus!) && (
                 <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                     <Typography variant="body2" fontWeight={500}>Information:</Typography>
                     <Button 
                        size="small" 
                        variant={bulkInfoType === InformationType.INFORMED ? 'contained' : 'outlined'}
                        onClick={() => setBulkInfoType(InformationType.INFORMED)}
                        sx={{ borderRadius: 2 }}
                     >
                         Informed
                     </Button>
                     <Button 
                        size="small" 
                        variant={bulkInfoType === InformationType.UNINFORMED ? 'contained' : 'outlined'}
                        onClick={() => setBulkInfoType(InformationType.UNINFORMED)}
                        color="error"
                        sx={{ borderRadius: 2 }}
                     >
                         Uninformed
                     </Button>
                 </Box>
            )}

            <TextField
               fullWidth
               size="small"
               placeholder="Bulk remarks"
               value={bulkRemarks}
               onChange={(e) => setBulkRemarks(e.target.value)}
               sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
           />
      </Box>
  );

  const renderScheduledOff = () => (
    <Box sx={{ pt: 1 }}>
        <Box sx={{ mb: 3 }}>
              <FormControlLabel
                  control={<Checkbox checked={selectAllActive} onChange={(e) => setSelectAllActive(e.target.checked)} />}
                  label={<Typography fontWeight={500}>Apply to Everyone</Typography>}
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 3.5, mt: -0.5 }}>
                  Apply to all active employees in school
              </Typography>
          </Box>

          {!selectAllActive && (
              <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>Add Employees</Typography>
                  {renderSearchField(handleAddEmployee, "Search to add...")}
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={1.5}>
                      {selectedEmployees.map(emp => (
                          <Chip key={emp._id} label={emp.name} onDelete={() => handleRemoveEmployee(emp._id)} size="small" />
                      ))}
                  </Box>
              </Box>
          )}

        <Divider sx={{ mb: 3 }} />

        <Typography variant="subtitle2" gutterBottom>Day Type</Typography>
        <Box display="flex" flexDirection="column" gap={2} mb={3}>
            {OFF_OPTIONS.map((opt) => (
                <Card 
                    key={opt.value}
                    onClick={() => setDayOffStatus(opt.value)}
                    variant="outlined"
                    sx={{ 
                        p: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        cursor: 'pointer',
                        borderColor: dayOffStatus === opt.value ? opt.color : 'divider',
                        bgcolor: dayOffStatus === opt.value ? alpha(opt.color, 0.05) : 'transparent',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: opt.color, bgcolor: alpha(opt.color, 0.02) }
                     }}
                >
                    <Avatar sx={{ bgcolor: alpha(opt.color, 0.1), color: opt.color }}>
                        <opt.icon />
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600} color={dayOffStatus === opt.value ? opt.color : 'text.primary'}>
                            {opt.label}
                        </Typography>
                    </Box>
                    {dayOffStatus === opt.value && (
                        <Box sx={{ ml: 'auto', color: opt.color }}>
                            {/* Check Icon or similar could go here */}
                             <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: opt.color }} />
                        </Box>
                    )}
                </Card>
            ))}
        </Box>

        <TextField
               fullWidth
               size="small"
               placeholder="Optional remarks (e.g. 'National Day')"
               value={bulkRemarks}
               onChange={(e) => setBulkRemarks(e.target.value)}
               sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
           />
    </Box>
  );

  return (
    <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
            sx: { borderRadius: 3, maxHeight: '85vh', maxWidth: 720 }
        }}
    >
        <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
            {mode !== 'select' && (
                <IconButton onClick={handleBack} size="small" edge="start">
                    <ArrowBack />
                </IconButton>
            )}
            <Box flex={1}>
                <Typography variant="h6" fontWeight={700} fontSize="1.1rem">
                    {mode === 'select' ? 'Mark Attendance' : MODE_OPTIONS.find(m => m.id === mode)?.title}
                </Typography>
            </Box>
            <DatePicker 
                value={selectedDate} 
                onChange={(d) => d && onDateChange(d)} 
                format="dd MMM yyyy"
                slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
                slots={{ openPickerIcon: CalendarMonth }}
            />
            <IconButton onClick={handleClose} size="small" edge="end">
                <Close />
            </IconButton>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 400 }}>
            {mode === 'select' && renderModeSelect()}
            {mode === 'individual' && renderIndividual()}
            {mode === 'bulk' && renderBulk()}
            {mode === 'scheduled-off' && renderScheduledOff()} 
        </DialogContent>
        {mode !== 'select' && (
            <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button onClick={handleClose} sx={{ borderRadius: 2 }}>Cancel</Button>
                <Button 
                    variant="contained" 
                    onClick={handleSave} 
                    disabled={isSaveDisabled || saving}
                    sx={{ borderRadius: 2, px: 4 }}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        )}
    </Dialog>
  );
};

export default MarkAttendanceModal;
